import { Controller } from '@hotwired/stimulus';
import { autoplayPermission } from '../../lib/autoplay_permission';
import { resolveGameViewClientId } from '../../lib/game_view_client';

export default class extends Controller {
  static targets = ['audio', 'command'];
  static values = { activeClientId: String, campaignId: Number, guideUrl: String };

  connect() {
    this.connected = true;
    this.audioElement = this.audioTarget;
    this.audioStates = this.audioElement.__soundEffectAudioStates ||= new Map();
    this.pendingUrls = this.audioElement.__pendingSoundEffectUrls ||= new Set();
    this.ownershipResolved = this.hasActiveClientIdValue || this.audioElement.__soundEffectOwnershipResolved || false;
    this.audioElement.__soundEffectOwnershipResolved = this.ownershipResolved;
    autoplayPermission.check(this.guideUrlValue);
    this.#updateLiveScreenState();
    this.commandTargets.forEach((command) => this.#consumeCommand(command));
  }

  disconnect() {
    this.connected = false;
    const audioElement = this.audioElement;

    queueMicrotask(() => {
      if (audioElement.isConnected) return;

      this.audioStates?.forEach((_state, audio) => {
        audio.pause?.();
        this.#removeAudio(audio);
      });
    });
  }

  activeClientIdValueChanged() {
    if (!this.connected) return;

    if (this.hasActiveClientIdValue) {
      this.ownershipResolved = true;
      this.audioElement.__soundEffectOwnershipResolved = true;
    }
    this.#updateLiveScreenState();
  }

  sessionStateUpdated({ detail: { campaignId, activeClientId } }) {
    if (!this.hasCampaignIdValue || Number(campaignId) !== this.campaignIdValue) return;

    this.ownershipResolved = true;
    this.audioElement.__soundEffectOwnershipResolved = true;
    if (activeClientId) {
      this.activeClientIdValue = activeClientId;
    } else {
      this.element.removeAttribute('data-game-screen--sound-effect-player-active-client-id-value');
      this.#updateLiveScreenState();
    }
  }

  commandTargetConnected(command) {
    if (this.connected) this.#consumeCommand(command);
  }

  // Both soundboard commands and automation outlets use the active screen's player.
  play(soundEffectUrl) {
    if (!this.connected || !this.#isLiveScreen() || !soundEffectUrl) return false;

    this.#playEffect(soundEffectUrl);
    return true;
  }

  #consumeCommand(command) {
    const { soundEffectUrl } = command.dataset;
    command.remove();
    if (!soundEffectUrl) return;

    if (this.#isLiveScreen()) {
      this.#playEffect(soundEffectUrl);
    } else if (!this.ownershipResolved) {
      this.pendingUrls.add(soundEffectUrl);
    }
  }

  #playEffect(soundEffectUrl) {
    if (!this.#isLiveScreen()) return;

    this.pendingUrls.delete(soundEffectUrl);
    this.#stopEffect(soundEffectUrl);

    const audio = this.audioElement.__audioFactory?.(soundEffectUrl) || new Audio(soundEffectUrl);
    const ended = () => this.#removeAudio(audio);
    const failed = () => {
      if (!this.audioStates.has(audio)) return;

      const error = audio.error || new Error('Sound effect failed to play.');
      this.#removeAudio(audio);
      this.#reportError(error);
    };

    audio.addEventListener('ended', ended, { once: true });
    audio.addEventListener('error', failed, { once: true });
    if (audio instanceof Node) this.audioElement.appendChild(audio);
    this.audioStates.set(audio, { ended, failed, soundEffectUrl });

    autoplayPermission.play(audio, {
      guideUrl: this.guideUrlValue,
      onError: (error) => {
        if (!this.audioStates.has(audio)) return;

        this.#removeAudio(audio);
        this.#reportError(error);
      }
    });
  }

  #stopEffect(soundEffectUrl) {
    this.audioStates.forEach((state, audio) => {
      if (state.soundEffectUrl !== soundEffectUrl) return;

      audio.pause?.();
      this.#removeAudio(audio);
    });
  }

  #removeAudio(audio) {
    const state = this.audioStates?.get(audio);
    if (state) {
      audio.removeEventListener('ended', state.ended);
      audio.removeEventListener('error', state.failed);
      this.audioStates.delete(audio);
    }

    autoplayPermission.forget(audio);
    audio.remove?.();
  }

  #updateLiveScreenState() {
    if (this.#isLiveScreen()) {
      this.pendingUrls.forEach((soundEffectUrl) => this.#playEffect(soundEffectUrl));
      this.dispatch('ready');
      return;
    }

    if (this.ownershipResolved) this.pendingUrls.clear();

    this.audioStates.forEach((_state, audio) => {
      audio.pause?.();
      this.#removeAudio(audio);
    });
  }

  #isLiveScreen() {
    return (
      this.hasActiveClientIdValue &&
      this.activeClientIdValue.length > 0 &&
      this.#currentClientId() === this.activeClientIdValue
    );
  }

  #currentClientId() {
    return this.hasCampaignIdValue ? resolveGameViewClientId(this.campaignIdValue) : null;
  }

  #reportError(error) {
    console.error('Sound effect playback failed.', error);
  }
}
